import React, { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { cn } from 'src/lib/utils';
import { isValidClientWebsite, normalizeClientWebsite } from '../../utils/client-url.utils';

const WEBSITE_HELPER =
  'Adding a website lets Studio automatically generate brand assets such as logos and colours.';

interface ClientWebsiteFieldProps {
  website: string | null | undefined;
  onSave: (website: string | null) => Promise<void>;
}

export const ClientWebsiteField: React.FC<ClientWebsiteFieldProps> = ({
  website,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(website ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(website ?? '');
    }
  }, [website, isEditing]);

  const commit = async() => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setError(null);
      setIsEditing(false);
      await onSave(null);
      return;
    }

    const normalized = normalizeClientWebsite(trimmed);
    if (!isValidClientWebsite(normalized)) {
      setError('Enter a valid URL');
      return;
    }

    setError(null);
    setIsEditing(false);
    await onSave(normalized);
  };

  const cancel = () => {
    setDraft(website ?? '');
    setError(null);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) {
              setError(null);
            }
          }}
          onBlur={() => {
            const trimmed = draft.trim();
            if (trimmed) {
              setDraft(normalizeClientWebsite(trimmed));
            }
            void commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          className={cn(portalDs.input, 'h-8 text-sm')}
          placeholder="https://example.com"
          autoFocus
        />
        {error ? (
          <p
            className="text-xs text-[#DC2626]"
            role="alert"
          >
            {error}
          </p>
        ) : (
          <p className={portalDs.hint}>
            {WEBSITE_HELPER}
          </p>
        )}
      </div>
    );
  }

  if (website) {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(portalDs.link, 'truncate')}
        >
          {website}
        </a>
        <button
          type="button"
          className="shrink-0 text-[#9CA3AF] hover:text-[#0D0F1A]"
          aria-label="Edit website"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
      <button
        type="button"
        className={cn(portalDs.hint, 'text-left hover:underline')}
        onClick={() => setIsEditing(true)}
      >
        Add website
      </button>
      <p className={portalDs.hint}>
        {WEBSITE_HELPER}
      </p>
    </div>
  );
};

export default ClientWebsiteField;
